import { useCallback } from 'react';
import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  BaseMemberMetaData,
  DependentFormData,
  FlagMarker,
  ManageMemberResponse,
  ManageMemberResult,
  MemberCreateRequest,
  MemberDetails,
  MemberDetailsFormData,
  MemberToggleStatusAction,
  MemberUpdateRequest,
  RelatedInstance
} from '../@types/Members';
import { useAxios } from '../context/AxiosContext';

import { transformIncomingMemberDetails } from '../utils/members/incomingDataTransformationHelper';
import { memberHasIndividual, memberIsEligibleForUpdates } from '../utils/members/memberDataUtils';
import {
  transformMemberDetailsToClientCommercialCreate,
  transformOutcomingDependentCreateRequest,
  transformOutcomingMemberUpdateRequest,
  transformOutgoingMemberStatusToggleRequest
} from '../utils/members/outcomingDataTransformationHelper';

export const MEMBER_QUERY_KEY_PREFIX: string = 'memberData';
export const MEMBERS_API_PATH: string = '/members';
export const MEMBER_LINKS_API_PATH: string = '/member-links';

interface Props {
  memberId?: string | null;
  primaryId?: string | null;
}

interface DependentCreateParams {
  primary: MemberDetails;
  dependent: DependentFormData;
}

interface MemberUpdateParams {
  primary: MemberDetails;
  memberToUpdate: MemberDetailsFormData;
}

export const useMemberDetails = ({ memberId, primaryId }: Props = {}) => {
  const { authAxios } = useAxios();

  const fetchMemberDetails = useCallback(
    async ({ id }) => {
      const response = await authAxios.get<MemberDetails>(`${MEMBERS_API_PATH}/details`, {
        params: {
          id
        }
      });

      return response.data;
    },
    [authAxios]
  );

  const getMemberInfo = useCallback(
    async ({ id }) => {
      try {
        return await fetchMemberDetails({ id });
      } catch (e) {
        toast.error(e.message, { position: 'top-center' });
      }
    },
    [fetchMemberDetails]
  );

  const getMemberData = useCallback(
    async ({ id }) => {
      try {
        const data = await fetchMemberDetails({ id });
        if (!data) {
          return undefined;
        }
        const enrichedData = primaryId ? { ...data, primaryId } : data;

        return transformIncomingMemberDetails(enrichedData);
      } catch (e) {
        toast.error(e.message, { position: 'top-center' });
      }
    },
    [fetchMemberDetails, primaryId]
  );

  const memberDataQuery = useQuery([MEMBER_QUERY_KEY_PREFIX, memberId], () => getMemberData({ id: memberId }), {
    refetchOnWindowFocus: false,
    enabled: !!memberId,
    suspense: true
  });

  const fetchDependents = useCallback(async () => {
    const memberData = await getMemberData({ id: memberId });

    return memberData?.relatedInstances || [];
  }, [getMemberData, memberId]);

  const createMember = useCallback(
    async (data: MemberCreateRequest | Array<MemberCreateRequest>) => {
      const { data: responseData } = await authAxios.post<ManageMemberResponse>(MEMBERS_API_PATH, {
        members: Array.isArray(data) ? data : [data]
      });
      return responseData;
    },
    [authAxios]
  );

  const updateMember = useCallback(
    async (data: MemberUpdateRequest | Array<MemberUpdateRequest>) => {
      const { data: responseData } = await authAxios.put<ManageMemberResponse>(MEMBERS_API_PATH, {
        members: Array.isArray(data) ? data : [data]
      });
      return responseData;
    },
    [authAxios]
  );

  const toggleMemberStatus = useCallback(
    async (memberId: string, action: MemberToggleStatusAction, effectiveEndDt?: string) => {
      const { data: responseData } = await authAxios.post<ManageMemberResponse>(
        `${MEMBERS_API_PATH}/toggle-status`,
        transformOutgoingMemberStatusToggleRequest({
          memberId,
          action,
          effectiveEndDt
        })
      );
      return responseData;
    },
    [authAxios]
  );

  const reactivateMember = useCallback(
    async ({ memberId }: { memberId: string }) => {
      return await toggleMemberStatus(memberId, MemberToggleStatusAction.Reactivate);
    },
    [toggleMemberStatus]
  );

  const terminateMember = useCallback(
    async ({ memberId, effectiveEndDt }: { memberId: string; effectiveEndDt?: string }) => {
      return await toggleMemberStatus(memberId, MemberToggleStatusAction.Terminate, effectiveEndDt);
    },
    [toggleMemberStatus]
  );

  /*
  This is used for adding an mrn id to an individual
   */
  const linkToExistingIndividual = useCallback(
    async (individualId: string, mrnId: string) => {
      const payload: any = { individualId, instances: [{ memberMasterMrn: mrnId }] };
      const { data: responseData } = await authAxios.post<ManageMemberResponse>(MEMBER_LINKS_API_PATH, payload);
      return responseData;
    },
    [authAxios]
  );

  /*
  This is used for creating a new individual and assigning mrns to that individual
   */
  const initiateMrnsLink = useCallback(
    async (mrnIds: string[]) => {
      const payload: any = { instances: mrnIds.map((mrnId: string) => ({ memberMasterMrn: mrnId })) };
      const { data: responseData } = await authAxios.post<ManageMemberResponse>(MEMBER_LINKS_API_PATH, payload);
      return responseData;
    },
    [authAxios]
  );

  const recreatePrimaryMemberWithLinks = useCallback(
    async (member: MemberDetails) => {
      const recreateDependent = async (mrnId: string, primaryMrn: string) => {
        try {
          const dependentDetails = await fetchMemberDetails({ id: mrnId });
          const dependentRecreatePayload = transformMemberDetailsToClientCommercialCreate(dependentDetails);
          // debating on shoving this whole thing in the above function
          let relationships: RelatedInstance[] = [];
          if (dependentRecreatePayload.relatedInstances)
            relationships = dependentRecreatePayload.relatedInstances.map((relatedInstanceData) => {
              const { relationshipTypeCd, relationshipSubtypeCd, relationshipStatusCd, dataSourceCd } =
                relatedInstanceData;
              return {
                relationshipTypeCd,
                relationshipSubtypeCd,
                relationshipStatusCd,
                dataSourceCd,
                mrnId: primaryMrn
              };
            });
          dependentRecreatePayload.relatedInstances = relationships;
          const newDependentResult = await createMember(dependentRecreatePayload);
          const newDependent: ManageMemberResult = newDependentResult.members[0];
          if (memberHasIndividual(dependentDetails)) {
            await linkToExistingIndividual(dependentDetails.individualId!, newDependent.memberMasterMrn);
          } else {
            await initiateMrnsLink([dependentDetails.memberMasterMrn!, newDependent.memberMasterMrn]);
          }
          return newDependent.memberMasterMrn;
        } catch (e) {
          throw new Error('Failed to recreate dependent member');
        }
      };
      // phase 1, re-creating the primary and linking
      const ccPayload = transformMemberDetailsToClientCommercialCreate(member);
      const ccMemberCreateResult = await createMember(ccPayload);
      if (!ccMemberCreateResult.members?.length) {
        throw new Error('Failed to recreate primary');
      }
      const newPrimary: ManageMemberResult = ccMemberCreateResult.members[0];
      // an odd thing happens here sometimes...it will come back without an individual but then send us an error saying its already linked
      // perhaps the elastic server is where individual id is coming from and its not getting updated?
      if (memberHasIndividual(member)) {
        await linkToExistingIndividual(member.individualId!, newPrimary.memberMasterMrn);
      } else {
        await initiateMrnsLink([member.memberMasterMrn!, newPrimary.memberMasterMrn]);
      }

      const oldToNewDependentsMap: Map<string, string> = new Map();
      if (member.primaryFlg === FlagMarker.Y && member.relatedInstances?.length) {
        // recreate dependents
        for (const { mrnId } of member.relatedInstances) {
          const newMrn: string = await recreateDependent(mrnId, newPrimary.memberMasterMrn);
          oldToNewDependentsMap.set(mrnId, newMrn);
        }
      }

      return {
        member: {
          ...member,
          memberMasterMrn: newPrimary.memberMasterMrn,
          mrnSourceClassificationCd: ccPayload.mrnSourceClassificationCd
        },
        dependentIdsMap: oldToNewDependentsMap
      };
    },
    [createMember, initiateMrnsLink, linkToExistingIndividual, fetchMemberDetails]
  );

  const createDependentMember = useCallback(
    async ({ primary, dependent }: DependentCreateParams) => {
      // if primary is client commercial
      if (memberIsEligibleForUpdates(primary)) {
        const transformedDependent: BaseMemberMetaData = transformOutcomingDependentCreateRequest(dependent, primary);
        return { result: await createMember(transformedDependent) };
      }

      const { member: newPrimary } = await recreatePrimaryMemberWithLinks(primary);

      const transformedDependent: BaseMemberMetaData = transformOutcomingDependentCreateRequest(dependent, newPrimary);
      return { result: await createMember(transformedDependent), newPrimaryId: newPrimary.memberMasterMrn };
    },
    [createMember, recreatePrimaryMemberWithLinks]
  );

  const updateMemberV2 = useCallback(
    async ({ primary, memberToUpdate }: MemberUpdateParams) => {
      const transformedData = transformOutcomingMemberUpdateRequest(memberToUpdate);

      let newPrimaryId;
      if (!memberIsEligibleForUpdates(primary)) {
        const { member: newPrimary, dependentIdsMap } = await recreatePrimaryMemberWithLinks(primary);
        transformedData.mrnSourceClassificationCd = newPrimary.mrnSourceClassificationCd;

        if (transformedData.primaryFlg === 'Y') {
          transformedData.memberMasterMrn = newPrimaryId = newPrimary.memberMasterMrn;
        } else {
          transformedData.memberMasterMrn = dependentIdsMap.get(transformedData.memberMasterMrn!);
          transformedData.relatedInstances =
            transformedData.relatedInstances?.map((relatedInstance) => ({
              ...relatedInstance,
              mrnId: newPrimary.memberMasterMrn
            })) || [];
        }
      }
      return { result: await updateMember(transformedData), newPrimaryId };
    },
    [recreatePrimaryMemberWithLinks, updateMember]
  );

  const createMemberMutation = useMutation(createMember);

  const updateMemberMutation = useMutation(updateMember);

  const reactivateMemberMutation = useMutation(reactivateMember);

  const terminateMemberMutation = useMutation(terminateMember);

  const createDependentMemberMutation = useMutation(createDependentMember);

  const memberUpdateV2Mutation = useMutation(updateMemberV2);

  return {
    member: memberDataQuery,
    loadDependentMembers: fetchDependents,
    createMember: createMemberMutation.mutateAsync,
    updateMember: updateMemberMutation.mutateAsync,
    isMemberMutationLoading:
      updateMemberMutation.isLoading ||
      createMemberMutation.isLoading ||
      createDependentMemberMutation.isLoading ||
      memberUpdateV2Mutation.isLoading,
    reactivateMember: reactivateMemberMutation.mutateAsync,
    terminateMember: terminateMemberMutation.mutateAsync,
    getMemberInfo,
    createDependentMember: createDependentMemberMutation.mutateAsync,
    updateMemberV2: memberUpdateV2Mutation.mutateAsync
  };
};
